import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowUp, ArrowDown, Play, Film } from "lucide-react";
import { Link } from "react-router-dom";
import VideoPreparing, { deviceCanPlayOriginal } from "@/components/reels/VideoPreparing";
import { useLang } from "@/lib/i18n";
import { fileUrl } from "@/lib/api";
import { fetchReelsForArticle } from "@/lib/reels";
import ReelsFeed from "@/components/reels/ReelsFeed";
import { pickQuality } from "@/components/reels/ReelVideo";
import VideoPlayer from "@/components/VideoPlayer";

/** Inside a story (approved preview): the story's own video first, then the curated reels — gestures INSIDE the player only.
 *  Below: «فيديوهات أخرى — اسحب لتحت داخل الفيديو» card with the next clip (or the previous one on the last clip). */
export default function ArticleReelPlayer({ article: incoming }) {
    const [article, setArticle] = useState(incoming);
    useEffect(() => setArticle(incoming), [incoming]);
    const { t, lang, p } = useLang();
    // Start with the story's own clip so playback begins immediately; the curated list replaces it when it arrives.
    const seed = (a) => (a?.video_url ? [{ ...a, likes: a.likes || 0, liked: !!a.liked }] : null);
    const [items, setItems] = useState(() => seed(incoming));
    const [index, setIndex] = useState(0);
    const [failed, setFailed] = useState(false);
    const [useOriginal, setUseOriginal] = useState(false);
    const wrap = useRef(null);
    const cursor = useRef(null);
    const loading = useRef(false);
    const [hasMore, setHasMore] = useState(false);

    useEffect(() => {
        let alive = true;
        setIndex(0); cursor.current = null;
        fetchReelsForArticle(article.id).then(({ items: list, next_cursor }) => {
            if (!alive) return;
            cursor.current = next_cursor; setHasMore(!!next_cursor);
            setItems((cur) => {
                if (!list.length) return cur?.length ? cur : [];
                return cur?.length === 1 && cur[0].id === list[0].id ? [{ ...list[0], ...cur[0], likes: list[0].likes, liked: list[0].liked }, ...list.slice(1)] : list;
            });
        }).catch(() => alive && setFailed(true));
        return () => { alive = false; };
    }, [article.id, lang]);
    const loadMore = useCallback(() => {
        if (!cursor.current || loading.current) return Promise.resolve();
        loading.current = true;
        return fetchReelsForArticle(article.id, cursor.current).then(({ items: more, next_cursor }) => {
            cursor.current = next_cursor; setHasMore(!!next_cursor);
            setItems((arr) => { const seen = new Set(arr.map((x) => x.id)); return [...arr, ...more.filter((x) => !seen.has(x.id))]; });
        }).catch(() => {}).finally(() => { loading.current = false; });
    }, [article.id]);

    // Renditions not ready yet: an H.264 original plays right away (same story, no duplicate — the ladder is pinned once
    // playback starts, so the arrival of the renditions never interrupts the viewer); otherwise the preparing state.
    const preparing = !article.video_web_url && !useOriginal && !deviceCanPlayOriginal(article) && ["pending", "processing", "failed"].includes(article.video_web_status || "");
    if (preparing) return <VideoPreparing article={article} onReady={(fresh) => setArticle((a) => ({ ...a, ...fresh }))} onPlayOriginal={() => setUseOriginal(true)} />;
    if ((failed && !items?.length) || (items && items.length === 0)) {
        return <VideoPlayer src={fileUrl((pickQuality() === "low" ? article.video_low_url : article.video_web_url) || article.video_web_url || article.video_url)} poster={(article.video_poster_url || article.cover_image) ? fileUrl(article.video_poster_url || article.cover_image) : undefined} slug={article.slug} articleId={article.id} />;
    }
    if (!items) return <div className="rounded-lg bg-[#11191c] animate-pulse mx-auto" style={{ aspectRatio: "9 / 16", maxWidth: 430 }} data-testid="article-reels-loading" />;

    const forward = index < items.length - 1;
    const target = items[index + (forward ? 1 : -1)];
    const targetTitle = target ? ((lang !== "ar" && target.translation?.title) || target.title) : null;
    const go = () => { setIndex(index + (forward ? 1 : -1)); wrap.current?.scrollIntoView({ block: "start", behavior: "smooth" }); };
    const onItemChange = (id, patch) => setItems((arr) => arr.map((x) => (x.id === id ? { ...x, ...patch } : x)));
    const Arrow = forward ? ArrowDown : ArrowUp;   // user-approved copy: «اسحب للأسفل للفيديو التالي ↓» (gesture itself unchanged)
    return (
        <div ref={wrap} className="mx-auto rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 shadow-[0_20px_55px_#13282e20]" style={{ maxWidth: 430, scrollMarginTop: 140 }} data-testid="article-reels">
            <ReelsFeed items={items} onItemChange={onItemChange} index={index} onIndexChange={setIndex} source="article" footer={false} onNeedMore={loadMore} hasMore={hasMore} />
            {target ? (
                <button type="button" onClick={go} aria-label={`${forward ? t("nextVideo") : t("prevVideo")}: ${targetTitle}`} data-testid="article-reels-next-button"
                    className="block w-full text-start bg-white dark:bg-[#1a292c] text-brand-black dark:text-gray-100 px-3.5 pt-3 pb-4 hover:bg-gray-50 dark:hover:bg-[#213237] transition-colors">
                    <span className="flex items-center gap-[7px] text-xs font-bold text-brand-red mb-3" data-testid="article-reels-next"><Arrow className="w-[17px] h-[17px] shrink-0" /><span>{forward ? t("reelsOtherVideos") : t("reelsPrevVideos")}</span></span>
                    <span className="flex items-center gap-2.5">
                        <span aria-hidden="true" className="block h-[72px] w-[57px] shrink-0 rounded-lg bg-gray-100 dark:bg-[#122023] bg-cover bg-center" style={(target.video_poster_url || target.cover_image) ? { backgroundImage: `url("${fileUrl(target.video_poster_url || target.cover_image)}")` } : undefined} />
                        <span className="min-w-0 flex-1 flex flex-col gap-1">
                            <span className="text-[11px] text-gray-500 dark:text-gray-400 font-medium">{forward ? t("nextVideo") : t("prevVideo")}</span>
                            <span className="text-[13px] leading-[1.7] font-bold line-clamp-2" style={{ textWrap: "balance" }}>{targetTitle}</span>
                        </span>
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#fff0f2] dark:bg-[#43232c] text-brand-red"><Play className="w-4 h-4" /></span>
                    </span>
                </button>
            ) : (
                <div className="bg-white dark:bg-[#1a292c] px-3.5 py-3 text-xs text-gray-500" data-testid="article-reels-no-more">{t("reelsNoMore")}</div>
            )}
            {article.video_web_url && (
                <Link to={`${p}/video/${article.slug}`} className="flex items-center gap-2 bg-white dark:bg-[#1a292c] border-t border-gray-100 dark:border-gray-700 px-3.5 py-2.5 text-xs font-bold text-brand-red no-underline hover:underline" data-testid="article-video-page-link"><Film className="w-4 h-4" /> {t("videoPageLink")}</Link>
            )}
        </div>
    );
}
